import React, { memo, useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';

import Overlay from 'components/Overlay';
import ModalSelectHeader from 'components/ModalSelectHeader';
import ModalSelectSearchInput from 'components/ModalSelectSearchInput';

import {
  Wrapper,
  CheckboxWrapper,
  BankCheckbox,
  BankLink,
  SaveButton,
  BanksList,
  OptionsTitle,
} from './styles';

const BanksSelect = memo(
  ({ banks, selectedBanksIds, onRequestChange, onRequestReset }) => {
    const { formatMessage } = useIntl();

    const [selectedIdsSet, selectBank] = useState(new Set(selectedBanksIds));
    const [changeInitialized, initChange] = useState(false);
    const [isFocused, changeFocus] = useState(false);
    const [searchValue, changeSearchValue] = useState('');

    const banksListRef = useRef();
    const searchRef = useRef();
    const headerRef = useRef();

    const headerTitle = useMemo(() => formatMessage({ id: 'addBank' }), []);
    const closeButtonTitle = useMemo(() => formatMessage({ id: 'close' }), []);
    const placeholder = useMemo(
      () =>
        formatMessage({
          id: 'addBankButton.placeholder',
        }),
      []
    );
    const cancelButtonLabel = useMemo(
      () =>
        formatMessage({
          id: 'currencyDropdown.searchInput.cancelButtonLabel',
        }),
      []
    );
    const saveLabel = useMemo(() => formatMessage({ id: 'save' }));

    const banksToShow = useMemo(
      () =>
        banks.filter(({ name }) =>
          name?.toLowerCase().includes(searchValue?.toLowerCase().trim())
        ),
      [banks, searchValue]
    );

    const selectedBanks = useMemo(
      () => banks.filter(({ id }) => selectedIdsSet.has(id)),
      [banks, selectedIdsSet]
    );

    const onFocus = useCallback(() => changeFocus(true), []);
    const onBlur = useCallback(() => changeFocus(false), []);

    const onCancel = useCallback(event => {
      event.stopPropagation();

      changeFocus(false);
      changeSearchValue('');
    }, []);

    const onSearchInputChange = useCallback(
      event => changeSearchValue(event.target.value),
      []
    );

    const onBankCheckboxChange = useCallback(
      event => {
        const bankId = parseInt(event.target.value, 10);
        const newSelectedBanksIds = new Set(selectedIdsSet);

        if (selectedIdsSet.has(bankId)) {
          newSelectedBanksIds.delete(bankId);
        } else {
          newSelectedBanksIds.add(bankId);
        }

        initChange(true);
        selectBank(newSelectedBanksIds);
      },
      [selectedIdsSet]
    );

    const onSaveBtnClick = useCallback(
      () => onRequestChange(Array.from(selectedIdsSet.values())),
      [onRequestChange, selectedIdsSet]
    );

    const onBanksListScroll = useCallback(() => {
      const { scrollTop } = banksListRef.current;

      if (scrollTop > 0) {
        if (searchRef.current && isFocused && searchValue) {
          searchRef.current.style.boxShadow =
            '0px 4px 24px rgba(0, 0, 0, 0.14)';
        }

        if (headerRef.current) {
          headerRef.current.style.boxShadow =
            '0px 4px 24px rgba(0, 0, 0, 0.14)';
        }
      }

      if (scrollTop === 0) {
        if (searchRef.current) {
          searchRef.current.style.boxShadow = '';
        }
        if (headerRef.current) {
          headerRef.current.style.boxShadow = '';
        }
      }
    }, [
      isFocused,
      searchValue,
      banksListRef.current,
      searchRef.current,
      headerRef.current,
    ]);

    return (
      <Wrapper>
        <ModalSelectHeader
          ref={headerRef}
          title={headerTitle}
          closeTitle={closeButtonTitle}
          onClose={onRequestReset}
        >
          <ModalSelectSearchInput
            ref={searchRef}
            isFocused={isFocused}
            value={searchValue}
            label={placeholder}
            placeholder={placeholder}
            cancelLabel={cancelButtonLabel}
            onChange={onSearchInputChange}
            onFocus={onFocus}
            onCancel={onCancel}
          />
        </ModalSelectHeader>

        <BanksList
          ref={banksListRef}
          isFocused={isFocused}
          onScroll={onBanksListScroll}
        >
          {!searchValue && selectedBanks.length > 0 && (
            <OptionsTitle>
              <FormattedMessage id="favorites" />
            </OptionsTitle>
          )}

          {!searchValue &&
            selectedBanks.map(({ name, id, logo }) => (
              <CheckboxWrapper key={id}>
                <BankCheckbox
                  value={id}
                  checked={selectedIdsSet.has(id)}
                  onChange={onBankCheckboxChange}
                  checkPosition="start"
                >
                  <BankLink name={name} logo={logo} />
                </BankCheckbox>
              </CheckboxWrapper>
            ))}

          {!searchValue && (
            <OptionsTitle>
              <FormattedMessage id="banks" />
            </OptionsTitle>
          )}

          {banksToShow.map(({ name, id, logo }) => (
            <CheckboxWrapper key={id}>
              <BankCheckbox
                value={id}
                checked={selectedIdsSet.has(id)}
                onChange={onBankCheckboxChange}
                checkPosition="start"
              >
                <BankLink name={name} logo={logo} />
              </BankCheckbox>
            </CheckboxWrapper>
          ))}
        </BanksList>

        <Overlay
          show={!searchValue.trim() && isFocused}
          offset={72}
          onClick={onBlur}
        />

        {changeInitialized && (
          <SaveButton type="button" onClick={onSaveBtnClick} title={saveLabel}>
            {saveLabel}
          </SaveButton>
        )}
      </Wrapper>
    );
  }
);

BanksSelect.propTypes = {
  banks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      logo: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedBanksIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  onRequestReset: PropTypes.func.isRequired,
  onRequestChange: PropTypes.func.isRequired,
};

export default BanksSelect;
